import { useEffect, useState } from "react";
import { useQuickEditRefresh } from "@/hooks/useQuickEditRefresh";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/ui-extra";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, Users, Send, Loader2, Trash2, Download } from "lucide-react";
import { BaseAssignControl } from "@/components/BaseAssignControl";
import { toast } from "sonner";
// pdfReport is dynamically imported at call time to keep jsPDF off the initial bundle.

export default function StaffPage() {
  const { isAdmin } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role: "groom", phone: "", telegram_user_id: "", rider_handicap: "", active: true });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const res = await api.get("/staff/"); setStaff(res.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useQuickEditRefresh(load);

  const assignBase = async (staffId, baseId) => {
    try {
      await api.patch(`/staff/${staffId}/base`, { base_id: baseId });
      setStaff((prev) => prev.map((s) => (s.id === staffId ? { ...s, base_id: baseId } : s)));
      toast.success("Base updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change base");
    }
  };

  const startEdit = (s) => {
    setEditing(s.id);
    setForm({
      name: s.name || "", role: s.role || "groom", phone: s.phone || "",
      telegram_user_id: s.telegram_user_id || "", rider_handicap: s.rider_handicap || "",
      active: s.active !== false,
    });
    setOpen(true);
  };
  const startNew = () => {
    setEditing(null);
    setForm({ name: "", role: "groom", phone: "", telegram_user_id: "", rider_handicap: "", active: true });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        telegram_user_id: form.telegram_user_id ? parseInt(form.telegram_user_id) : null,
        rider_handicap: form.rider_handicap ? parseFloat(form.rider_handicap) : null,
      };
      if (editing) await api.put(`/staff/${editing}`, payload);
      else await api.post("/staff/", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const exportPdf = async () => {
    try {
      const rows = staff.map((s) => ({
        name: s.name,
        role: s.role,
        phone: s.phone || "",
        handicap: s.rider_handicap ?? "",
        active: s.active !== false ? "Active" : "Inactive",
      }));
      const { downloadReport } = await import("@/lib/pdfReport");
      await downloadReport({
        eyebrow: "OPERATIONS",
        title: "Staff Roster",
        period: "All staff with WhatsApp and contact bindings",
        filename: "staff-roster",
        summary: [
          { label: "Total", value: staff.length },
          { label: "Active", value: staff.filter((s) => s.active !== false).length },
        ],
        columns: [
          { header: "Name", dataKey: "name" },
          { header: "Role", dataKey: "role" },
          { header: "Phone", dataKey: "phone", mono: true },
          { header: "Handicap", dataKey: "handicap", mono: true, halign: "right" },
          { header: "Status", dataKey: "active", status: true },
        ],
        rows,
      });
    } catch (e) {
      toast.error("Could not generate PDF");
      console.error(e);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Staff"
        subtitle={`${staff.length} member${staff.length===1?"":"s"}. add a phone number to auto-bind their WhatsApp on first message.`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={exportPdf}
              disabled={loading || staff.length === 0}
              data-testid="staff-download-pdf-button"
            >
              <Download className="h-4 w-4 mr-2" /> Download PDF
            </Button>
            <Button onClick={startNew} data-testid="staff-add-button"><Plus className="h-4 w-4 mr-2" /> Add staff</Button>
          </div>
        }
      />
      <Card className="rounded-xl overflow-hidden">
        <div className="hidden md:block overflow-auto" data-testid="staff-table">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--ap-paper))]">
              <tr className="text-xs uppercase tracking-[0.14em] text-muted-foreground border-b">
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Phone</th>
                <th className="text-left px-4 py-3">WhatsApp</th>
                <th className="text-left px-4 py-3">Telegram</th>
                <th className="text-right px-4 py-3">Handicap</th>
                <th className="text-left px-4 py-3">Base</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</td></tr>
              ) : staff.length === 0 ? (
                <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No staff yet. Add manually or have them message <span className="font-mono">+27 71 911 0938</span> on WhatsApp.</td></tr>
              ) : staff.map((s) => (
                <tr key={s.id} onClick={() => startEdit(s)} className="border-b hover:bg-accent/60 cursor-pointer" data-testid={`staff-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium">{s.name}</td>
                  <td className="px-4 py-3 text-muted-foreground capitalize">{s.role}</td>
                  <td className="px-4 py-3 text-muted-foreground">{s.phone || <span className="text-amber-600 text-xs">add to bind WA</span>}</td>
                  <td className="px-4 py-3">
                    {s.whatsapp_wa_id
                      ? <span className="text-xs text-emerald-700 font-medium" title={`Bound: +${s.whatsapp_wa_id}`}>● bound</span>
                      : s.phone
                        ? <span className="text-xs text-muted-foreground">awaiting first msg</span>
                        : <span className="text-xs text-muted-foreground"></span>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground font-mono text-xs">{s.telegram_user_id || ""}</td>
                  <td className="px-4 py-3 text-right text-muted-foreground">{s.rider_handicap ?? ""}</td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <BaseAssignControl
                      value={s.base_id}
                      onAssign={(bid) => assignBase(s.id, bid)}
                      size="xs"
                      testid={`staff-base-${s.id}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    {s.pending_approval ? <span className="text-xs text-[hsl(var(--ap-warning))]">pending</span>
                      : s.active === false ? <span className="text-xs text-muted-foreground">inactive</span>
                      : <span className="text-xs text-[hsl(var(--ap-success))]">active</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile staff cards */}
        <div className="md:hidden divide-y" data-testid="staff-cards">
          {loading ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</div>
          ) : staff.length === 0 ? (
            <div className="px-4 py-12 text-center text-sm text-muted-foreground">
              No staff yet. Add manually or have them message <span className="font-mono">+27 71 911 0938</span> on WhatsApp.
            </div>
          ) : staff.map((s) => (
            <div
              key={s.id}
              role="button"
              tabIndex={0}
              onClick={() => startEdit(s)}
              onKeyDown={(e) => { if (e.key === "Enter") startEdit(s); }}
              className="w-full text-left px-4 py-3 hover:bg-accent/60 active:bg-accent/80 transition-colors cursor-pointer"
              data-testid={`staff-card-${s.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-muted-foreground capitalize">{s.role}</div>
                  <div className="text-[11px] text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2">
                    {s.phone ? (
                      <span className="font-mono">{s.phone}</span>
                    ) : (
                      <span className="text-amber-600">add phone to bind WA</span>
                    )}
                    {s.whatsapp_wa_id && (
                      <>
                        <span>·</span>
                        <span className="text-emerald-700">● WA bound</span>
                      </>
                    )}
                    {s.rider_handicap != null && (
                      <>
                        <span>·</span>
                        <span>hcp {s.rider_handicap}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-2">
                  {s.pending_approval ? <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ap-warning))]">pending</span>
                    : s.active === false ? <span className="text-[10px] uppercase tracking-wider text-muted-foreground">inactive</span>
                    : <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--ap-success))]">active</span>}
                  <div onClick={(e) => e.stopPropagation()}>
                    <BaseAssignControl
                      value={s.base_id}
                      onAssign={(bid) => assignBase(s.id, bid)}
                      size="xs"
                      testid={`staff-base-m-${s.id}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit staff" : "Add staff member"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["groom","rider","manager","vet","driver","other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Telegram user ID</Label>
              <Input value={form.telegram_user_id} onChange={(e) => setForm({ ...form, telegram_user_id: e.target.value })} data-testid="staff-telegram-id-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Rider handicap</Label>
              <Input type="number" step="0.5" value={form.rider_handicap} onChange={(e) => setForm({ ...form, rider_handicap: e.target.value })} />
            </div>
          </div>
          <DialogFooter className="flex sm:justify-between gap-2">
            {editing && isAdmin ? (
              <Button
                type="button"
                variant="destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={saving || deleting}
                data-testid="staff-delete-button"
              >
                <Trash2 className="h-4 w-4 mr-1.5" /> Delete
              </Button>
            ) : <div />}
            <Button onClick={submit} disabled={saving} data-testid="staff-submit-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this staff member?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the record and the WhatsApp / Telegram
              binding. Their historical audit-log entries remain.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="staff-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="staff-delete-confirm"
              onClick={async () => {
                if (!editing) return;
                setDeleting(true);
                try {
                  await api.delete(`/staff/${editing}`);
                  toast.success("Staff member deleted");
                  setConfirmDelete(false);
                  setOpen(false);
                  await load();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Delete failed");
                } finally {
                  setDeleting(false);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
